import { notification } from '../../../components/Notifications'
import { HttpStatusCode } from '../../../consts/httpStatusCode'
import { formatErrorMessage } from '../../../helpers/errorResponseGenerate'
import { type DisplaySettings } from '../components/DisplayCustomizationDrawer/index'
import { chartSettingsSwitches, keyStatSwitches } from './constants'

export const formatReportPayload = (data: DisplaySettings): { calculations: string[], compareIds: any[], sliceIds: any[] } => ({
  calculations: Object.keys(data.keyStatistics || {}).filter(key => data.keyStatistics[key]),
  compareIds: (data.comparisons || [])
    .filter(item => item.comparison)
    .map(item => item.comparison),
  sliceIds: data.slices?.map((item: any) => item.attributeValueId) || []
})

export const getFinalReportPayload = (
  payload?: Record<string, any>,
  settings?: any
): Record<string, any> => {
  if (payload && Object.keys(payload).length > 0) {
    return payload
  }
  return formatReportPayload(settings) ?? {}
}

// Utility to check if any value is added/changed in DisplaySettings (except chartSettings)
export const isDisplaySettingsChanged = (settings: DisplaySettings, initial: DisplaySettings): boolean => {
  const hasComparison = settings.comparisons.some(
    c => c.compareBy !== null || c.comparison !== null
  )
  const hasSlice = settings.slices.some(
    s => s.sliceBy !== null
  )
  const hasKeyStat = Object.values(settings.keyStatistics).some(Boolean)
  return hasComparison || hasSlice || hasKeyStat
}

export const getMaxLabelLength = (isFullScreen: boolean, xLabelCount: number): number => {
  if (xLabelCount > 3) return isFullScreen ? 18 : 12
  return 34
}

export const truncateLabel = (label: string, maxLen: number): string =>
  label.length > maxLen ? label.slice(0, maxLen - 3) + '...' : label

// Format number for tooltip: show decimals only if needed
export const formatValue = (val: any): string => {
  if (typeof val !== 'number') return String(val)
  if (Number.isInteger(val)) return val.toString()
  return val % 1 === 0 ? val.toString() : val.toFixed(2).replace(/\.00$/, '')
}

// yAxis label formatter for LineChart
export const yAxisLabelFormatter = (
  value: number | string,
  isTopOrBottom2: boolean,
  questionType: any,
  report: any
): string => {
  if (isTopOrBottom2) {
    return `${value}%`
  }
  let label = String(value)
  if (questionType === 'LinearScale' || questionType === 3) {
    const idx = report.options?.findIndex((opt: any) => opt.name === value)
    const weight = idx !== undefined && idx > -1 ? report.options[idx].weights?.find((w: any) => w != null) : undefined
    label = weight !== undefined ? `${label} (${weight})` : label
  }
  if (label.length > 25) {
    label = label.slice(0, 23) + '...'
  }
  return label
}

export const getDefaultSettings = (): DisplaySettings => ({
  comparisons: [{ compareBy: null, comparison: null }],
  slices: [],
  keyStatistics: keyStatSwitches.reduce<Record<string, boolean>>((acc, { name }) => {
    acc[name] = false
    return acc
  }, {}),
  chartSettings: chartSettingsSwitches.reduce<Record<string, boolean>>((acc, { name }) => {
    acc[name] = false
    return acc
  }, {})
})

export const getFilteredLinearScaleOptions = (options: any[], notApplicableValue: string): any[] =>
  options.filter(opt => opt.name !== notApplicableValue)

export const handleReportNotifications = (
  reportError: any,
  completionRateError: any,
  isReportsError: boolean,
  isCompletionRateError: boolean,
  isReportsPending: boolean,
  completionRateLoading: boolean
): void => {
  const FALLBACK = 'Unknown error occurred'
  const reportStatus = reportError?.status
  const rateStatus = completionRateError?.status

  if (!reportStatus && !rateStatus && isReportsPending && completionRateLoading) return

  const { BAD_REQUEST, NOT_FOUND, UNPROCESSABLE_ENTITY } = HttpStatusCode

  // Both not found
  if (
    reportStatus === NOT_FOUND &&
    rateStatus === NOT_FOUND &&
    isReportsError &&
    isCompletionRateError
  ) {
    notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'error' })
    return
  }

  // Bad request and unprocessable entity
  if (
    reportStatus === BAD_REQUEST &&
    rateStatus === UNPROCESSABLE_ENTITY &&
    isReportsError &&
    isCompletionRateError
  ) {
    notification(formatErrorMessage(completionRateError?.message, FALLBACK), { variant: 'error' })
    return
  }

  // Only report is bad request
  if (
    reportStatus === BAD_REQUEST &&
    isReportsError &&
    rateStatus !== UNPROCESSABLE_ENTITY
  ) {
    notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'info' })
    return
  }

  // Only report is not found
  if (reportStatus === NOT_FOUND && isReportsError) {
    notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'error' })
    return
  }

  // Only report is unprocessable entity
  if (
    reportStatus === UNPROCESSABLE_ENTITY &&
    isReportsError &&
    !isCompletionRateError
  ) {
    notification(formatErrorMessage(reportError?.message, FALLBACK), { variant: 'error' })
  }
}
